import React, { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';

const AuthContext = createContext(null);

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem('token'));
  const [loading, setLoading] = useState(true);
  
  // For multi-office login flow
  const [pendingLogin, setPendingLogin] = useState(null);

  useEffect(() => {
    if (token) {
      fetchCurrentUser();
    } else {
      setLoading(false);
    }
  }, [token]);

  const fetchCurrentUser = async () => {
    try {
      const response = await axios.get(`${API}/auth/me`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUser(response.data);
    } catch (error) {
      console.error('Failed to fetch user:', error);
      logout();
    } finally {
      setLoading(false);
    }
  };

  const login = async (email, password, selectedOfficeId = null) => {
    const payload = { email, password };
    if (selectedOfficeId) {
      payload.selected_office_id = selectedOfficeId;
    }
    
    const response = await axios.post(`${API}/auth/login`, payload);
    const data = response.data;
    
    // Check if office selection is required
    if (data.requires_office_selection) {
      setPendingLogin({
        email,
        password,
        availableOffices: data.available_offices,
        tempToken: data.temp_token
      });
      return { requiresOfficeSelection: true, availableOffices: data.available_offices };
    }
    
    // Normal login flow
    const { access_token, user: userData } = data;
    setToken(access_token);
    setUser(userData);
    setLoading(false); // Ensure loading is false after successful login
    localStorage.setItem('token', access_token);
    setPendingLogin(null);
    return { requiresOfficeSelection: false, user: userData };
  };

  const completeLoginWithOffice = async (selectedOfficeId) => {
    if (!pendingLogin) {
      throw new Error('No pending login');
    }
    
    const result = await login(pendingLogin.email, pendingLogin.password, selectedOfficeId);
    return result;
  };

  const cancelOfficeSelection = () => {
    setPendingLogin(null);
  };

  const logout = () => {
    setToken(null);
    setUser(null);
    setPendingLogin(null);
    localStorage.removeItem('token');
  };

  const setPassword = async (token, password) => {
    await axios.post(`${API}/auth/set-password`, { token, password });
  };

  const resetPassword = async (email) => {
    await axios.post(`${API}/auth/reset-password`, { email });
  };

  return (
    <AuthContext.Provider value={{ 
      user, 
      token, 
      loading, 
      login, 
      logout, 
      setPassword, 
      resetPassword,
      pendingLogin,
      completeLoginWithOffice,
      cancelOfficeSelection
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

export default AuthContext;